import React, { useState, useContext } from "react";
import "./css/Payment.css";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  CardNumberElement,
  CardExpiryElement,
  CardCvcElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import { LanguageContext } from './LanguageContext';

const stripePromise = loadStripe("pk_live_51RAQ2RHTLBgAn6mbI9OxQHg7oBiijfadlSrcab4IRFVdYvZlJeUGnzChXjMD78hLydjs3kSupTWJlnPyVvbqIbVM00xlPs0M3k");

const COURSE_SLUG = "personal-finance";
const BASE_PRICE = 49.99;
const INFLUENCER_DISCOUNT = 10;
const INFLUENCER_COUPONS = new Set(["ANA10", "MARIA10", "JOAO10", "LI10", "LETYS10",]);

function getCouponPricing(couponCode) {
  const normalizedCouponCode = String(couponCode || "").trim().toUpperCase();
  const hasDiscount = INFLUENCER_COUPONS.has(normalizedCouponCode);
  const discount = hasDiscount ? INFLUENCER_DISCOUNT : 0;

  return {
    normalizedCouponCode,
    hasDiscount,
    discount,
    total: BASE_PRICE - discount,
  };
}

function CheckoutForm() {
  const stripe = useStripe();
  const elements = useElements();
  const [message, setMessage] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [couponCode, setCouponCode] = useState("");
  const { language, toggleLanguage, translations } = useContext(LanguageContext);
  const pricing = getCouponPricing(couponCode);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsProcessing(true);
    setMessage("");

    if (!stripe || !elements) {
      setMessage("Stripe is not ready. Please try again in a moment.");
      setIsProcessing(false);
      return;
    }

    try {
      const API_URL = process.env.REACT_APP_API_URL || "http://localhost:3001";
      const res = await fetch(`${API_URL}/payment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ fullName, email, couponCode, course: COURSE_SLUG }),
      });

      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || "Failed to initiate payment.");
      }

      const { clientSecret } = await res.json();

      const result = await stripe.confirmCardPayment(clientSecret, {
        payment_method: {
          card: elements.getElement(CardNumberElement),
          billing_details: {
            name: fullName,
            email: email,
          },
        },
      });

      if (result.error) {
        setMessage(`${translations.paymentFailed}: ${result.error.message}`);
      } else if (result.paymentIntent.status === "succeeded") {
        setMessage(translations.paymentSucceeded);
      } else {
        setMessage(translations.paymentUnexpected);
      }
    } catch (err) {
      setMessage(`${translations.paymentError}: ${err.message}`);
    }

    setIsProcessing(false);
  };

  return (
    <div className="page-wrapper">
      {/* Language Selector */}
      <button className="language-button" onClick={toggleLanguage}>
        {language === "en" ? "🇧🇷 Português" : "🇺🇸 English"}
      </button>
    <div className="payment-container">
      <h2 className="payment-title">{translations.paymentDetails}</h2>
      <h3 className="payment-class">{translations.personalFinanceCourse}</h3>

      {!message && (
        <>
          <p style={{ textAlign: "center", fontSize: "14px", color: "#666", marginBottom: "20px" }}>
            {translations.sigUp} <strong>{translations.personalClasses}</strong>. {translations.team}<strong> {translations.business}</strong> {translations.after}
          </p>

          <div className="card-icons">
            <img src="/img/cards/visa.png" alt="Visa" />
            <img src="/img/cards/mastercard.png" alt="Mastercard" />
            <img src="/img/cards/amex.png" alt="Amex" />
            <img src="/img/cards/discover.png" alt="Discover" />
          </div>

          <form onSubmit={handleSubmit}>
            <label htmlFor="fullName">{translations.fullName}</label>
            <input
              id="fullName"
              type="text"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              required
            />

            <label htmlFor="email">{translations.email}</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />

            <label htmlFor="card-number">{translations.cardNumber}</label>
            <div className="card-box">
              <CardNumberElement id="card-number" options={{ style: { base: { fontSize: "16px" } } }} />
            </div>

            <label>{translations.expirationDateCVC}</label>
            <div className="card-row">
              <div className="card-box">
                <CardExpiryElement id="card-expiry" options={{ style: { base: { fontSize: "16px" } } }} />
              </div>
              <div className="card-box">
                <CardCvcElement id="card-cvc" options={{ style: { base: { fontSize: "16px" } } }} />
              </div>
            </div>

            <label htmlFor="couponCode">{translations.couponCode}</label>
            <input
              id="couponCode"
              type="text"
              value={couponCode}
              onChange={(e) => setCouponCode(e.target.value)}
              placeholder="Enter coupon if any"
            />

            <div className="payment-summary">
              <div className="payment-summary-row">
                <span>{translations.priceLabel}</span>
                <span>${BASE_PRICE.toFixed(2)}</span>
              </div>
              {pricing.hasDiscount && (
                <div className="payment-summary-row discount">
                  <span>
                    {translations.couponApplied}: {pricing.normalizedCouponCode}
                  </span>
                  <span>-${pricing.discount.toFixed(2)}</span>
                </div>
              )}
              <div className="payment-summary-row total">
                <span>{translations.totalLabel}</span>
                <span>${pricing.total.toFixed(2)}</span>
              </div>
            </div>

            <button disabled={!stripe || isProcessing}>
              {isProcessing ? translations.processingPayment : `${translations.payButton} $${pricing.total.toFixed(2)}`}
            </button>
          </form>
        </>
      )}

      {message && (
        <div className="payment-message">

          <p>{message}</p>
        </div>
      )}
    </div>
    </div>
  );
}

function App() {
  return (
    <Elements stripe={stripePromise}>
      <CheckoutForm />
    </Elements>
  );
}

export default App;
